import { List } from '../domain/list/ListDomains';
import { AuditLog, AuditLogType } from '../domain/rbac/RbacDomains';
import ValueFactory from '../util/ValueFactory';
import ListDataService from '../service/ListDataService';
import ListService from '../service/ListService';
import MessageUtil from '../util/MessageUtil';

export interface ListHistoryMeta {
  truncated: boolean;
  hasGap: boolean;
}

export interface ListHistoryPreview {
  list: List;
  historyItems: Record<string, any>[];
  historyMeta: ListHistoryMeta;
}

// Shared by ListFileEditorContainer and ListQueryEditorContainer: ADD_LIST_DATA/UPDATE_LIST_DATA/
// REMOVE_LIST_DATA audit records carry the changed list *item*, not a full List entity (no
// fields/type) — applying that document as the List would corrupt the Schema tab. For those, the
// real List is loaded via a direct REST lookup (NOT the local reactive repository — a freshly
// opened preview tab/window has no active subscription, so a bare repository.find() against the
// unsynced local store silently returns nothing and falls back to a blank "new list" look). For
// every operation type, the list's items are also reconstructed as of that audit's timestamp.
export async function prepareListPreview(
  log: AuditLog,
  listService: ListService,
  listDataService: ListDataService,
): Promise<ListHistoryPreview> {
  const isItemMutation =
    log.operation === AuditLogType.ADD_LIST_DATA ||
    log.operation === AuditLogType.UPDATE_LIST_DATA ||
    log.operation === AuditLogType.REMOVE_LIST_DATA;
  const listId = log.document.id;

  let list: List;
  if (isItemMutation) {
    list = (await listService.findById(listId)) ?? ValueFactory.initializeList();
  } else {
    const document = JSON.parse(log.document.data);
    if (document == undefined || typeof document != 'object') {
      console.error('Document is not valid', document);
      list = ValueFactory.initializeList();
    } else {
      list = document;
    }
  }

  let historyItems: Record<string, any>[] = [];
  let historyMeta = { truncated: false, hasGap: false };
  try {
    const history = await listDataService.getHistory(listId, log.timestamp);
    historyItems = history.items;
    historyMeta = { truncated: history.truncated, hasGap: history.hasGap };
  } catch (e) {
    console.error(e);
    MessageUtil.showError('Failed to load historical list data');
  }

  return { list, historyItems, historyMeta };
}
